import { Link } from 'react-router';
import { Heart, Star, Languages, Clock, ArrowRight } from 'lucide-react';
import { LANGUAGE_LABELS } from './constants';
import type { PublicTutorCard } from '@/lib/schemas/tutor-profile';

interface TutorCardProps {
  tutor: PublicTutorCard;
  isSaved: boolean;
  onToggleSave: (tutorId: string) => void;
}

export function TutorCard({ tutor, isSaved, onToggleSave }: TutorCardProps) {
  return (
    <div className="relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 group card-shadow">
      {/* Heart shortcut button (Shortlist Toggle) */}
      <button
        onClick={() => onToggleSave(tutor.id)}
        className={`absolute top-4 right-4 z-10 p-1 cursor-pointer transition-all duration-300 hover:scale-110 ${
          isSaved
            ? 'text-red-500 dark:text-red-400'
            : 'text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400'
        }`}
        aria-label={isSaved ? 'Remove from shortlist' : 'Save to shortlist'}
      >
        <Heart
          className={`h-4 w-4 transition-all duration-300 ${
            isSaved ? 'fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400' : ''
          }`}
        />
      </button>

      <div className="flex flex-col sm:flex-row gap-5 sm:items-stretch justify-between">
        {/* Left Column: Avatar & rating */}
        <div className="flex sm:flex-col items-center gap-3 shrink-0 self-center sm:self-start">
          <div className="relative">
            {tutor.photoUrl ? (
              <img
                src={tutor.photoUrl}
                alt={tutor.displayName}
                className="h-16 w-16 rounded-full object-cover ring-2 ring-brand-500/20"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-accent-500 text-xl font-bold text-white shadow-md ring-2 ring-brand-500/20">
                {tutor.displayName.charAt(0).toUpperCase()}
              </div>
            )}
            {tutor.weeklyAvailabilityHours > 0 && (
              <span className="absolute bottom-0 right-0 block h-3.5 w-3.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900" title="Active availability" />
            )}
          </div>

          {/* Desktop rating badge */}
          <div className="hidden sm:flex flex-col items-center gap-0.5 mt-1">
            {tutor.averageRating != null ? (
              <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-955/20 px-2 py-0.5 rounded-md border border-amber-100/30">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                <span className="text-xs font-bold text-amber-700 dark:text-amber-400">
                  {tutor.averageRating.toFixed(1)}
                </span>
              </div>
            ) : (
              <span className="inline-flex items-center rounded-md border border-brand-500/40 dark:border-brand-500/30 px-1.5 py-0.5 text-[9px] font-bold text-brand-600 dark:text-brand-500 select-none">
                NEW
              </span>
            )}
          </div>
        </div>

        {/* Middle Column: Tutor text details */}
        <div className="min-w-0 flex-1 space-y-2.5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/tutors/${tutor.id}`}
                className="font-bold text-lg text-slate-900 hover:text-brand-600 dark:text-slate-100 dark:hover:text-brand-400 cursor-pointer transition-colors"
              >
                {tutor.displayName}
              </Link>
              
              {/* Mobile rating badge */}
              {tutor.averageRating != null ? (
                <div className="sm:hidden flex items-center gap-1 bg-amber-50 dark:bg-amber-955/20 px-1.5 py-0.5 rounded border border-amber-100/30">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  <span className="text-2xs font-bold text-amber-700 dark:text-amber-400">
                    {tutor.averageRating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <span className="sm:hidden inline-flex items-center rounded-md border border-brand-500/40 dark:border-brand-500/30 px-1.5 py-0.5 text-[9px] font-bold text-brand-600 dark:text-brand-500 select-none">
                  NEW
                </span>
              )}
            </div>

            {tutor.headline && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5 font-medium line-clamp-1">
                {tutor.headline}
              </p>
            )}
          </div>

          {/* Stats summary row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500 dark:text-slate-400">
            {tutor.languagesSpoken.length > 0 && (
              <div className="flex items-center gap-1">
                <Languages className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="truncate">
                  Speaks:{' '}
                  <span className="font-semibold text-slate-700 dark:text-slate-300">
                    {tutor.languagesSpoken.map((lang) => LANGUAGE_LABELS[lang] || lang).join(', ')}
                  </span>
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span>
                Weekly hours:{' '}
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {tutor.weeklyAvailabilityHours} hrs
                </span>
              </span>
            </div>
          </div>

          {/* Subject & Grade level badges */}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {tutor.subjects.map((s) => (
              <span
                key={s}
                className="rounded-lg bg-accent-50 dark:bg-accent-955/20 px-2.5 py-0.5 text-xs font-semibold text-accent-700 dark:text-accent-400 border border-accent-100/10"
              >
                {s}
              </span>
            ))}
            {tutor.gradeLevels.map((g) => (
              <span
                key={g}
                className="rounded-lg bg-brand-50 dark:bg-brand-955/20 px-2.5 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-400 border border-brand-100/10"
              >
                {g}
              </span>
            ))}
          </div>
        </div>

        {/* Right Column: Rate chip centered vertically, View Profile button at bottom */}
        <div className="flex flex-col justify-between items-end gap-2 shrink-0 w-full sm:w-auto mt-4 sm:mt-0 min-h-[110px]">
          <div className="flex-1 flex items-center justify-end w-full translate-y-3">
            <div className="inline-flex items-center gap-2 bg-brand-50 dark:bg-brand-955/40 px-2.5 py-1 rounded-xl border border-brand-100/30 dark:border-brand-900/30 shadow-2xs">
              <span className="text-[10px] font-extrabold text-brand-600 dark:text-brand-400 uppercase tracking-widest border-r border-brand-200/50 dark:border-brand-800/50 pr-2">
                Rate
              </span>
              <span className="text-xs font-black text-brand-600 dark:text-brand-400">
                ${Math.round(tutor.hourlyRate.usdMinor / 100)}
                <span className="text-[10px] font-semibold text-slate-505 dark:text-slate-400 ml-0.5">/hr</span>
              </span>
            </div>
          </div>
          <Link
            to={`/tutors/${tutor.id}`}
            className="inline-flex items-center justify-center rounded-xl bg-slate-950 hover:bg-slate-850 dark:bg-brand-600 dark:hover:bg-brand-700 px-5 py-2 text-xs font-bold text-white shadow-sm transition-all cursor-pointer w-full sm:w-auto text-center"
          >
            <span>View Profile</span>
            <ArrowRight className="ml-1 h-3.5 w-3.5 shrink-0" />
          </Link>
        </div>
      </div>
    </div>
  );
}
